/**
 * PanelCheckbox — a node's own switch, in its header row.
 *
 * IT REPLACES PanelToggle IN THE TREE, and the reason is the tree. A two-position Off/On track is a
 * fine control for one setting on a card; seventeen of them down a nested list read as seventeen
 * separate decisions, and the indentation stops being legible against 92 px of switch on every row.
 * A tickbox is the shape a hierarchy of on/off things already has everywhere else.
 *
 * DISABLED READS AS LOCKED, NOT AS OFF, which is the same distinction PanelToggle draws and for the
 * same reason: the editor greys a node whose parent is off, and if that looked identical to
 * unticked the user would tap it and wonder why nothing happened. So a locked box keeps its own
 * state and loses only its contrast, and the caller passes `title` to say why.
 */

import { Check } from 'lucide-react';
import { ACCENT_BG, ACCENT_ON, MOTION } from './tokens';

interface PanelCheckboxProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  /** Why it is locked — surfaced as a tooltip. */
  title?: string;
  ariaLabel?: string;
}

export function PanelCheckbox({ value, onChange, disabled, title, ariaLabel }: PanelCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={value}
      aria-label={ariaLabel}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!value)}
      className={`w-5 h-5 shrink-0 rounded-md grid place-items-center ring-1 ring-inset ${MOTION} ${
        disabled ? 'opacity-40' : 'cursor-pointer'
      } ${
        value ? `${ACCENT_BG} ring-black/10` : 'bg-neutral-100 ring-black/10 hover:ring-black/30'
      }`}
    >
      <Check size={13} strokeWidth={3.5} className={`${ACCENT_ON} ${MOTION} ${value ? '' : 'opacity-0'}`} />
    </button>
  );
}
